import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import SchedulesCard from "examples/Cards/SchedulesCard/SchedulesCard";
import Goals_Table from "layouts/dashboard/components/Goals_Table";
import OrdersOverview from "layouts/dashboard/components/OrdersOverview";
import TechniqueCard from "layouts/dashboard/components/TechniqueCard";
import useUserStats from "layouts/dashboard/data/weeklystreak";

import apiClient from "utils/apiClient";
import { getAuthToken } from "utils/authToken";
import { ENDPOINTS } from "utils/endpoints";
import { RED, GREEN } from "assets/theme/brandColors";

const STREAK_RULE_COPY =
  "Your streak counts weeks, not days. Cooking again in the same week keeps it where it is; " +
  "cooking in the following week adds one. Skip a full week and it resets to 1 — there's no grace period.";

const DAY_MS = 24 * 60 * 60 * 1000;

function formatStreakDate(iso) {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" });
}

/**
 * Turns the streak window from /me/stats into a concrete nudge.
 *
 * `streak_keep_by` is the last date a cook still keeps the streak alive;
 * `streak_grows_from` is the first date a cook increments it rather than
 * counting as the same week. Before that date the honest message is "you're
 * safe" — cooking today would not move the number.
 *
 * Returns `{ urgent, text, detail }`. `text` is what the card shows and has to
 * survive a skim; `detail` is the date arithmetic, which goes in the tooltip
 * behind the existing "?". Both dates are worth having — a tester who wanted
 * them found them useful — but "Cook from Tuesday, Aug 18 to make it 2 — the
 * last day that counts is Monday, Aug 24" is two dates and a sum in the place
 * where the card should be saying one thing. When the streak is at risk the
 * message is already short and carries no detail: that is the case where the
 * date IS the message.
 */
function buildStreakNudge(stats) {
  if (!stats?.streak || !stats.streak_keep_by) return null;

  const keepBy = new Date(`${stats.streak_keep_by}T00:00:00`);
  if (Number.isNaN(keepBy.getTime())) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const daysLeft = Math.round((keepBy - today) / DAY_MS);
  if (daysLeft < 0) return null; // already lapsed — next cook restarts at 1

  const keepByLabel = formatStreakDate(stats.streak_keep_by);
  if (!keepByLabel) return null;

  const growsFrom = stats.streak_grows_from
    ? new Date(`${stats.streak_grows_from}T00:00:00`)
    : null;
  const notYetGrowing = growsFrom && !Number.isNaN(growsFrom.getTime()) && today < growsFrom;

  if (notYetGrowing) {
    const growsLabel = formatStreakDate(stats.streak_grows_from);
    return {
      urgent: false,
      text: "Safe this week 🔥",
      detail: `Cook from ${growsLabel} to make it ${
        stats.streak + 1
      } — the last day that counts is ${keepByLabel}.`,
    };
  }

  if (daysLeft === 0) {
    return {
      urgent: true,
      text: `Last day — cook today to keep your ${stats.streak}-week streak.`,
    };
  }

  return {
    urgent: daysLeft <= 3,
    text: `Cook again by ${keepByLabel} (${daysLeft} day${
      daysLeft === 1 ? "" : "s"
    } left) to keep your streak.`,
  };
}

function Dashboard() {
  const [schedules, setSchedules] = useState([]);
  const [loadingSchedules, setLoadingSchedules] = useState(true);
  const [userStats, setUserStats] = useState(null);
  const [xpBarFlash, setXpBarFlash] = useState(false);
  const [recentGoalUpdates, setRecentGoalUpdates] = useState(null);
  const [goalToast, setGoalToast] = useState({ open: false, message: "" });
  const prevPointsRef = useRef(null);
  const goalUpSeenRef = useRef(false);
  const navigate = useNavigate();
  const token = getAuthToken();

  useEffect(() => {
    if (!token) navigate("/authentication/sign-in", { replace: true });
  }, [navigate]);

  const weeklyStreak = useUserStats(token);

  const fetchSchedules = async () => {
    try {
      const res = await apiClient.get(ENDPOINTS.schedule.programs);
      if (res.status === 200 && Array.isArray(res.data)) {
        setSchedules(
          res.data.map((item) => ({
            ...item,
            name: item.title,
          }))
        );
      } else {
        setSchedules([]);
      }
    } catch {
      setSchedules([]);
    } finally {
      setLoadingSchedules(false);
    }
  };

  const fetchUserStats = async () => {
    try {
      const res = await apiClient.get(ENDPOINTS.users.stats, { params: { check_level_up: true } });
      if (res.status === 200) {
        const newPoints = res.data.points ?? 0;
        if (prevPointsRef.current !== null && newPoints > prevPointsRef.current) {
          setXpBarFlash(true);
          setTimeout(() => setXpBarFlash(false), 1800);
        }
        prevPointsRef.current = newPoints;
        setUserStats(res.data);
        // Capture goal_up once — backend clears it after first read
        if (!goalUpSeenRef.current && res.data.goal_up) {
          goalUpSeenRef.current = true;
          setRecentGoalUpdates(res.data.goal_up);
        }
      }
    } catch {
      // silently fail — stats are non-critical
    }
  };

  useEffect(() => {
    if (!recentGoalUpdates || recentGoalUpdates.length === 0) return;
    const completed = recentGoalUpdates.filter((u) => u.completed);
    if (completed.length === 0) return;
    const message =
      completed.length === 1
        ? `${completed[0].title}  ·  +${completed[0].xp_awarded} XP`
        : `${completed.length} goals completed  ·  +${completed.reduce(
            (s, u) => s + (u.xp_awarded || 0),
            0
          )} XP`;
    setGoalToast({ open: true, message });
  }, [recentGoalUpdates]);

  const handleDeleteSchedule = async (id) => {
    try {
      await apiClient.delete(ENDPOINTS.schedule.deleteProgram(id));
      fetchSchedules();
    } catch (err) {
      console.error("Failed to delete schedule:", err.message);
    }
  };

  useEffect(() => {
    fetchSchedules();
    fetchUserStats();

    const handleVisibility = () => {
      if (document.visibilityState === "visible") fetchUserStats();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  const level = userStats?.computed_level ?? userStats?.level ?? 1;
  const points = userStats?.points ?? 0;
  const currentFloor = userStats?.current_level_floor_xp ?? 0;
  const nextCeiling = userStats?.next_level_total_xp ?? 1000;
  const xpInLevel = points - currentFloor;
  const xpSpan = Math.max(1, nextCeiling - currentFloor);
  const progressPercent = userStats?.progress_percent ?? 0;
  const streak = weeklyStreak ?? userStats?.streak ?? 0;
  const streakNudge = buildStreakNudge(userStats);
  const distinction = userStats?.distinction ?? "Novice";
  const username = userStats?.username ?? "";

  const activeSchedules = schedules.filter(
    (s) => s.total_count === 0 || s.completed_count < s.total_count
  );
  const hasSchedules = !loadingSchedules && activeSchedules.length > 0;
  const isNewUser = !loadingSchedules && schedules.length === 0;
  const allDone = !loadingSchedules && !isNewUser && activeSchedules.length === 0;

  return (
    <DashboardLayout>
      <DashboardNavbar username={username} distinction={distinction} />

      <MDBox py={3}>
        {/* ── Row 1: Schedules + XP/Streak ── */}
        <Grid container spacing={3} mb={3}>
          {/* Schedules — wide */}
          <Grid item xs={12} lg={9}>
            {hasSchedules && (
              <SchedulesCard schedules={activeSchedules} onDelete={handleDeleteSchedule} />
            )}

            {allDone && (
              <Card
                sx={{
                  background: "linear-gradient(135deg, #fff5f5 0%, #ffeaea 100%)",
                  border: "1.5px solid #a01c27",
                  borderRadius: "14px",
                  textAlign: "center",
                  p: { xs: 4, md: 6 },
                }}
              >
                <MDTypography variant="h2" mb={1}>
                  🎉
                </MDTypography>
                <MDTypography variant="h4" fontWeight="bold" color="error" mb={1}>
                  All caught up!
                </MDTypography>
                <MDTypography variant="body2" color="text" mb={3}>
                  You&apos;ve completed all your active schedules.
                  <br />
                  Ready to keep the momentum going?
                </MDTypography>
                <MDButton
                  variant="contained"
                  size="large"
                  onClick={() => navigate("/schedule-builder")}
                  sx={{
                    bgcolor: RED,
                    color: "#fff",
                    px: 4,
                    "&:hover": { bgcolor: "#7d1520" },
                  }}
                >
                  Build a new schedule →
                </MDButton>
              </Card>
            )}

            {isNewUser && (
              <Card
                sx={{
                  background: "linear-gradient(135deg, #fff5f5 0%, #ffeaea 100%)",
                  border: "1.5px solid #a01c27",
                  borderRadius: "14px",
                  textAlign: "center",
                  p: { xs: 4, md: 6 },
                }}
              >
                <MDTypography variant="h2" mb={1}>
                  🍳
                </MDTypography>
                <MDTypography variant="h4" fontWeight="bold" color="error" mb={1}>
                  Build your first schedule
                </MDTypography>
                <MDTypography variant="body2" color="text" mb={3}>
                  Pick recipes, set your week, and start cooking.
                  <br />
                  Your XP and streak begin with your first completed recipe.
                </MDTypography>
                <MDButton
                  variant="contained"
                  size="large"
                  onClick={() => navigate("/schedule-builder")}
                  sx={{
                    bgcolor: RED,
                    color: "#fff",
                    px: 4,
                    "&:hover": { bgcolor: "#7d1520" },
                  }}
                >
                  Get started →
                </MDButton>
              </Card>
            )}
          </Grid>

          {/* XP / Level / Streak — compact */}
          <Grid item xs={12} lg={3}>
            <Card sx={{ height: "100%", p: 3 }}>
              {/* Level + XP */}
              <MDBox mb={3}>
                <MDTypography variant="overline" color="secondary">
                  Progress
                </MDTypography>
                <MDTypography variant="h4" fontWeight="bold" sx={{ color: RED, lineHeight: 1.2 }}>
                  Level {level}
                </MDTypography>
                <MDTypography
                  variant="caption"
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: 400,
                    color: "#555",
                    textTransform: "uppercase",
                    letterSpacing: 1,
                  }}
                >
                  {distinction}
                </MDTypography>

                <MDBox
                  mt={1.5}
                  mb={0.5}
                  sx={{ height: 8, bgcolor: "#f0f0f0", borderRadius: 4, overflow: "hidden" }}
                >
                  <MDBox
                    sx={{
                      width: `${progressPercent}%`,
                      height: "100%",
                      bgcolor: xpBarFlash ? GREEN : RED,
                      borderRadius: 4,
                      transition: "width 0.4s, background-color 0.4s",
                      boxShadow: xpBarFlash ? "0 0 8px rgba(67,160,71,0.6)" : "none",
                    }}
                  />
                </MDBox>
                <MDTypography
                  variant="caption"
                  color="secondary"
                  sx={{ fontSize: "0.8rem", fontWeight: 400, color: "#555" }}
                >
                  {xpInLevel} / {xpSpan} XP to Level {level + 1}
                </MDTypography>

                {/* The level number on its own says nothing about where it
                    leads. The ladder — and the distinction at the end of it —
                    lives on Your Kitchen, so point at it from here. */}
                <MDBox
                  mt={1}
                  onClick={() => navigate("/your-kitchen")}
                  sx={{
                    cursor: "pointer",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    color: RED,
                    fontFamily: "Roboto, sans-serif",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  See your ladder →
                </MDBox>
              </MDBox>

              {/* Streak */}
              <MDBox textAlign="center" pt={1} borderTop="1px solid #f0f0f0">
                <MDBox display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                  <MDTypography
                    variant="h3"
                    fontWeight="bold"
                    sx={{ color: streak > 0 ? "#ff6d00" : "#ccc", fontSize: "2.5rem" }}
                  >
                    🔥 {streak}
                  </MDTypography>
                  {/* The rule, plus this week's dates when there are any. One
                      "?" rather than two: the arithmetic is an instance of the
                      rule, not a separate thing to explain. */}
                  <Tooltip
                    title={
                      streakNudge?.detail ? (
                        <>
                          {STREAK_RULE_COPY}
                          <MDBox mt={1}>{streakNudge.detail}</MDBox>
                        </>
                      ) : (
                        STREAK_RULE_COPY
                      )
                    }
                    enterTouchDelay={0}
                    leaveTouchDelay={6000}
                  >
                    <Icon
                      sx={{ fontSize: 16, color: "#999", cursor: "help" }}
                      aria-label="How the streak works"
                    >
                      help_outline
                    </Icon>
                  </Tooltip>
                </MDBox>
                <MDTypography
                  variant="caption"
                  color="secondary"
                  sx={{ fontSize: "0.8rem", fontWeight: 400, color: "#555", display: "block" }}
                >
                  {streak > 0 ? "week streak" : "start your streak!"}
                </MDTypography>

                {streakNudge && (
                  <MDBox
                    mt={1}
                    px={1.25}
                    py={0.75}
                    sx={{
                      bgcolor: streakNudge.urgent ? "#fff4e5" : "#f5f5f5",
                      border: `1px solid ${streakNudge.urgent ? "#ffb74d" : "#e6e6e6"}`,
                      borderRadius: "8px",
                    }}
                  >
                    <MDTypography
                      variant="caption"
                      sx={{
                        display: "block",
                        fontSize: "0.72rem",
                        lineHeight: 1.45,
                        color: streakNudge.urgent ? "#b35309" : "#666",
                        fontWeight: streakNudge.urgent ? 600 : 400,
                      }}
                    >
                      {streakNudge.text}
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        {/* ── Row 2: Goals + Achievements ── */}
        <Grid container spacing={3} alignItems="stretch" mb={3}>
          <Grid item xs={12} lg={9}>
            <Goals_Table recentGoalUpdates={recentGoalUpdates} />
          </Grid>
          <Grid item xs={12} lg={3}>
            <OrdersOverview
              justLevelUp={userStats?.just_level_up ?? false}
              justDistinctionUp={userStats?.just_distinction_up ?? false}
              computedLevel={userStats?.computed_level ?? null}
              distinction={userStats?.distinction ?? null}
              nextDistinction={userStats?.next_distinction ?? null}
            />
          </Grid>
        </Grid>

        {/* ── Row 3: Technique skill ── */}
        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12} lg={3}>
            <TechniqueCard />
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* Goal completion toast — z-index kept below the level-up Dialog (1300) */}
      <Snackbar
        open={goalToast.open}
        autoHideDuration={4500}
        onClose={() => setGoalToast((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        sx={{ zIndex: 1100 }}
      >
        <Alert
          onClose={() => setGoalToast((s) => ({ ...s, open: false }))}
          severity="success"
          variant="filled"
          sx={{
            width: "100%",
            bgcolor: GREEN,
            color: "#fff",
            "& .MuiAlert-icon": { color: "#fff" },
          }}
        >
          <strong>Goal complete</strong> — {goalToast.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default Dashboard;
